import { nameWords, wholeWordPattern } from "./name-match"

/**
 * Words that point at a common label even when the label's own name is not in
 * the text. A group only fires when the workspace has a label with one of its
 * names, so a team without a Bug label never sees one suggested.
 */
const LABEL_KEYWORDS: { names: string[]; triggers: string[] }[] = [
	{
		names: ["bug", "bugs", "defect", "bug report"],
		triggers: [
			"bug",
			"broken",
			"crash",
			"crashed",
			"crashing",
			"error",
			"fail",
			"failed",
			"failing",
			"failure",
			"fix",
			"regression",
			"exception",
			"doesn't work",
			"does not work",
			"not working",
			"stopped working",
		],
	},
	{
		names: ["feature", "feature request", "new feature"],
		triggers: [
			"feature request",
			"would be nice",
			"would love",
			"add support",
			"support for",
			"ability to",
			"allow us to",
		],
	},
	{
		names: ["improvement", "enhancement"],
		triggers: ["improve", "improvement", "enhance", "enhancement", "polish"],
	},
	{
		names: ["performance", "perf", "speed"],
		triggers: [
			"slow",
			"slowly",
			"slowness",
			"lag",
			"laggy",
			"latency",
			"timeout",
			"timed out",
			"freezes",
			"hangs",
		],
	},
	{
		names: ["security"],
		triggers: [
			"vulnerability",
			"vulnerable",
			"xss",
			"csrf",
			"injection",
			"exploit",
		],
	},
	{
		names: ["documentation", "docs", "doc"],
		triggers: ["docs", "documentation", "readme", "typo"],
	},
	{
		names: ["design", "ui", "ux", "ui/ux"],
		triggers: ["design", "layout", "styling", "css", "misaligned"],
	},
	{
		names: ["mobile", "ios", "android"],
		triggers: ["mobile", "ios", "android", "iphone", "ipad"],
	},
	{
		names: ["chore", "maintenance", "tech debt"],
		triggers: [
			"chore",
			"refactor",
			"cleanup",
			"clean up",
			"upgrade",
			"bump",
			"tech debt",
		],
	},
	{
		names: ["question"],
		triggers: ["how do i", "how can i", "is it possible"],
	},
]

// Names that fit any text, so seeing the word says nothing.
const GENERIC_NAMES = new Set([
	"other",
	"general",
	"misc",
	"miscellaneous",
	"none",
	"new",
	"test",
])

// Project names often start with a word that says nothing on its own ("New
// onboarding", "Customer portal"), so a short form never starts with one.
const PREFIX_STOPWORDS = new Set([
	"customer",
	"customers",
	"user",
	"users",
	"issue",
	"issues",
	"feature",
	"features",
	"improve",
	"improvements",
	"update",
	"updates",
	"support",
	"product",
	"general",
	"project",
])

/** Linear priorities: 1 Urgent, 2 High. Text that asks for one, most urgent first. */
const PRIORITY_KEYWORDS: { priority: number; triggers: string[] }[] = [
	{
		priority: 1,
		triggers: [
			"urgent",
			"urgently",
			"asap",
			"downtime",
			"outage",
			"is down",
			"went down",
			"blocked",
			"blocking",
			"blocker",
			"critical",
			"emergency",
		],
	},
	{
		priority: 2,
		triggers: ["important", "high priority"],
	},
]

// "not urgent" and "no blocker" ask for the opposite.
const NEGATION_BEFORE = /(?:\bnot|\bisn't|\bno|\bnon|\bnothing)[\s-]+$/i

export const MAX_QUICK_SUGGESTIONS = 4

const KIND_ORDER = ["label", "project", "priority", "assignee"] as const

export interface QuickSuggestionLabel {
	id: string
	name: string
	/** Null for a workspace label, which every team can use. */
	teamId: string | null
}

export interface QuickSuggestionProject {
	id: string
	name: string
}

export interface QuickSuggestionMember {
	id: string
	name: string
	displayName?: string | null
}

export type QuickSuggestion<
	L extends QuickSuggestionLabel = QuickSuggestionLabel,
	P extends QuickSuggestionProject = QuickSuggestionProject,
	M extends QuickSuggestionMember = QuickSuggestionMember,
> =
	| { kind: "label"; label: L; index: number }
	| { kind: "project"; project: P; index: number }
	| { kind: "priority"; priority: number; index: number }
	| { kind: "assignee"; member: M; index: number }

function firstIndex(text: string, patterns: (RegExp | null)[]): number {
	let best = -1
	for (const pattern of patterns) {
		const match = pattern?.exec(text)
		if (match && (best === -1 || match.index < best)) best = match.index
	}
	return best
}

function firstUnnegatedIndex(text: string, patterns: (RegExp | null)[]) {
	let best = -1
	for (const pattern of patterns) {
		if (!pattern) continue
		const global = new RegExp(pattern.source, `${pattern.flags}g`)
		for (const match of text.matchAll(global)) {
			if (NEGATION_BEFORE.test(text.slice(0, match.index))) continue
			if (best === -1 || match.index < best) best = match.index
			break
		}
	}
	return best
}

/**
 * Leading words of a project name that point at it alone, so "Mobile" finds
 * "Mobile app". Each short form needs a distinctive first word and must not
 * start another project's name too.
 */
function projectShortForms(
	project: QuickSuggestionProject,
	projects: readonly QuickSuggestionProject[],
): string[] {
	const words = nameWords(project.name)
	const first = words[0]
	if (!first || first.replace(/[^\p{L}\p{N}]/gu, "").length < 4) return []
	if (PREFIX_STOPWORDS.has(first) || GENERIC_NAMES.has(first)) return []

	const others = projects
		.filter((p) => p.id !== project.id)
		.map((p) => nameWords(p.name))
	const forms: string[] = []
	for (let length = 1; length < words.length; length++) {
		const prefix = words.slice(0, length)
		const shared = others.some((other) =>
			prefix.every((word, i) => other[i] === word),
		)
		if (!shared) forms.push(prefix.join(" "))
	}
	return forms
}

function startsUppercase(text: string, index: number) {
	const char = text[index] ?? ""
	return char !== char.toLowerCase()
}

/**
 * Where the text names a teammate: their full name anywhere, or a first or
 * display name that only they have. A lone name must be capitalised or follow
 * "@", so "will" and "mark" in a sentence don't count.
 */
function memberIndex(
	text: string,
	member: QuickSuggestionMember,
	members: readonly QuickSuggestionMember[],
): number {
	const full = nameWords(member.name)
	let best =
		full.length > 1 ? firstIndex(text, [wholeWordPattern(member.name)]) : -1

	const short = new Set(
		[full[0], ...nameWords(member.displayName ?? "")].filter(
			(word): word is string => !!word && word.length >= 3,
		),
	)
	for (const word of short) {
		const sharedWith = members.some(
			(other) =>
				other.id !== member.id &&
				(nameWords(other.name)[0] === word ||
					nameWords(other.displayName ?? "").includes(word)),
		)
		if (sharedWith) continue
		const pattern = wholeWordPattern(word)
		if (!pattern) continue
		for (const match of text.matchAll(
			new RegExp(pattern.source, `${pattern.flags}g`),
		)) {
			const mentioned =
				text[match.index - 1] === "@" || startsUppercase(text, match.index)
			if (!mentioned) continue
			if (best === -1 || match.index < best) best = match.index
			break
		}
	}
	return best
}

/**
 * Labels, a project, a priority and teammates the text of a new issue points
 * at, in the order the text names them, at most four. Pure keyword matching, cheap enough to run on
 * every keystroke.
 */
export function quickSuggestions<
	L extends QuickSuggestionLabel,
	P extends QuickSuggestionProject,
	M extends QuickSuggestionMember = QuickSuggestionMember,
>({
	text,
	labels,
	projects,
	teamId,
	selectedLabelIds,
	selectedProjectId,
	priorities = [],
	selectedPriority,
	members = [],
	selectedAssigneeId,
}: {
	text: string
	labels: readonly L[]
	projects: readonly P[]
	teamId: string
	selectedLabelIds: readonly string[]
	selectedProjectId?: string | null
	/** The priorities the workspace offers; one missing here is never suggested. */
	priorities?: readonly number[]
	selectedPriority?: number | null
	members?: readonly M[]
	selectedAssigneeId?: string | null
}): QuickSuggestion<L, P, M>[] {
	const normalized = text.replace(/[‘’]/g, "'")
	if (normalized.trim().length === 0) return []

	// A workspace and a team label often share a name; offer one, the team's.
	const byName = new Map<string, L[]>()
	for (const label of labels) {
		const key = nameWords(label.name).join(" ")
		if (!key || GENERIC_NAMES.has(key)) continue
		byName.set(key, [...(byName.get(key) ?? []), label])
	}

	const selected = new Set(selectedLabelIds)
	const found: QuickSuggestion<L, P, M>[] = []
	for (const [key, group] of byName) {
		if (group.some((label) => selected.has(label.id))) continue
		const label = group.find((l) => l.teamId === teamId) ?? group[0]!
		const triggers = LABEL_KEYWORDS.filter((k) => k.names.includes(key))
			.flatMap((k) => k.triggers)
			.map(wholeWordPattern)
		const index = firstIndex(normalized, [
			wholeWordPattern(label.name),
			...triggers,
		])
		if (index !== -1) found.push({ kind: "label", label, index })
	}

	for (const project of projects) {
		if (project.id === selectedProjectId) continue
		const key = nameWords(project.name).join(" ")
		if (key.replace(/[^\p{L}\p{N}]/gu, "").length < 3) continue
		if (GENERIC_NAMES.has(key)) continue
		const index = firstIndex(normalized, [
			wholeWordPattern(project.name),
			...projectShortForms(project, projects).map(wholeWordPattern),
		])
		if (index !== -1) found.push({ kind: "project", project, index })
	}

	// 0 is No priority, so any real priority outranks it.
	const isMoreUrgent = (priority: number) =>
		!selectedPriority || priority < selectedPriority
	for (const { priority, triggers } of PRIORITY_KEYWORDS) {
		if (!priorities.includes(priority)) continue
		const index = firstUnnegatedIndex(
			normalized,
			triggers.map(wholeWordPattern),
		)
		if (index === -1) continue
		if (isMoreUrgent(priority))
			found.push({ kind: "priority", priority, index })
		break
	}

	for (const member of members) {
		if (member.id === selectedAssigneeId) continue
		const index = memberIndex(normalized, member, members)
		if (index !== -1) found.push({ kind: "assignee", member, index })
	}

	return found
		.sort(
			(a, b) =>
				a.index - b.index ||
				KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind),
		)
		.slice(0, MAX_QUICK_SUGGESTIONS)
}
